import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import swal from "sweetalert";
import '../styles/registeruser.css';
import { BASE_URL } from "../App";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPhone, faCheckCircle, faExclamationTriangle, faUser, faGraduationCap } from "@fortawesome/free-solid-svg-icons";

// ===== دوال الحماية من XSS لتسجيل دخول ولي الأمر =====

// تنظيف المدخلات
const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  
  return input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/vbscript:/gi, '')
    .replace(/<[^>]*>/g, '')
    .replace(/[<>'"&]/g, (match) => {
      const entityMap = {
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#x27;',
        '&': '&amp;'
      };
      return entityMap[match];
    });
};

// فحص أكواد XSS
const detectXSS = (input) => {
  if (typeof input !== 'string') return false;
  
  const xssPatterns = [
    /alert\s*\(/i,
    /confirm\s*\(/i,
    /prompt\s*\(/i,
    /document\./i,
    /window\./i,
    /eval\s*\(/i,
    /<script/i,
    /<iframe/i,
    /<object/i,
    /javascript:/i,
    /vbscript:/i,
    /on\w+\s*=/
  ];
  
  return xssPatterns.some(pattern => pattern.test(input));
};

// التحقق من صحة البيانات
const validateDataType = (input, fieldName) => {
  if (typeof input !== 'string') return false;
  
  const maxLengths = { pphone: 15 };
  if (maxLengths[fieldName] && input.length > maxLengths[fieldName]) return false;
  
  const suspiciousPatterns = [
    /<[^>]*>/,
    /javascript:/i,
    /union\s+select/i,
    /drop\s+table/i,
    /\.\./
  ];
  
  return !suspiciousPatterns.some(pattern => pattern.test(input));
};

// دالة التنظيف الشاملة
const secureParentInput = (input, fieldName) => {
  if (!input || typeof input !== 'string') return input;
  
  if (detectXSS(input)) {
    console.warn(`XSS attempt detected in parent field: ${fieldName}`);
    return '';
  }
  
  if (!validateDataType(input, fieldName)) {
    console.warn(`Invalid data type in parent field: ${fieldName}`);
    return '';
  }
  
  return sanitizeInput(input.trim());
};

// Validation functions
const validateField = (name, value) => {
  const errors = {};

  switch (name) {
    case 'pphone':
      if (!value.trim()) {
        errors[name] = 'رقم ولي الأمر مطلوب';
      } else if (!/^01[0125][0-9]{8}$/.test(value.trim())) {
        errors[name] = 'يجب إدخال رقم هاتف مصري صحيح';
      }
      break;

    default:
      break;
  }

  return errors;
};

const validateForm = (formData) => {
  const errors = {};
  
  Object.keys(formData).forEach(field => {
    const fieldErrors = validateField(field, formData[field]);
    if (fieldErrors[field]) {
      errors[field] = fieldErrors[field];
    }
  });

  return errors;
};

export const ParentLogin = ({ setLoggedIn, style }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    pphone: ""
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [studentData, setStudentData] = useState(null);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    
    // تطبيق الحماية من XSS على المدخلات
    const securedValue = secureParentInput(value, name);
    
    // إذا تم حذف المحتوى بسبب XSS، أظهر تحذير
    if (value && !securedValue && value.trim() !== '') {
      swal("تحذير أمني!", "تم رفض المدخلات لاحتوائها على محتوى غير آمن", "warning");
      return;
    }
    
    setFormData(prev => ({ ...prev, [name]: securedValue }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    
    // Validate field on blur
    const fieldErrors = validateField(name, value);
    if (fieldErrors[name]) {
      setErrors(prev => ({ ...prev, [name]: fieldErrors[name] }));
    }
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    
    // فحص أمني شامل قبل الإرسال
    const securityCheck = () => {
      let hasSecurityIssue = false;
      const value = formData.pphone;
      if (value && typeof value === 'string') {
        if (detectXSS(value)) {
          console.error(`XSS detected in parent phone:`, value);
          hasSecurityIssue = true;
        }
        if (!validateDataType(value, 'pphone')) {
          console.error(`Invalid data type in parent phone:`, value);
          hasSecurityIssue = true;
        }
      }
      return !hasSecurityIssue;
    };
    
    // تشغيل فحص الأمان
    if (!securityCheck()) {
      swal({
        title: "خطأ أمني!",
        text: "تم اكتشاف محتوى غير آمن في بيانات تسجيل الدخول.",
        icon: "error",
        button: "حسناً",
      });
      return;
    }
    
    // Validate form
    const formErrors = validateForm(formData);
    
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      setTouched(Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {}));
      
      swal({
        title: "خطأ في البيانات!",
        text: "يرجى مراجعة البيانات المدخلة وإصلاح الأخطاء المذكورة.",
        icon: "error",
        button: "حسناً",
      });
      
      return;
    }

    setLoading(true);
    setErrors({});
    
    try {
      // تنظيف البيانات مرة أخيرة قبل الإرسال
      const sanitizedData = {
        pphone: secureParentInput(formData.pphone, 'pphone')
      };
      
      const response = await axios.post(`${BASE_URL}/api/student-profile-by-parent-phone`, sanitizedData);
      
      if (response.data.success) {
        setStudentData(response.data.data);
        
        // تنظيف البيانات القديمة أولاً
        localStorage.removeItem("token");
        localStorage.removeItem("sha1");
        localStorage.removeItem("studentData");
        localStorage.removeItem("parentPhone");
        localStorage.removeItem("userType");
        
        // حفظ بيانات الطالب في localStorage
        localStorage.setItem("studentData", JSON.stringify(response.data.data));
        localStorage.setItem("parentPhone", formData.pphone);
        localStorage.setItem("userType", "parent");

        swal({
          title: "تم تسجيل دخول ولي الأمر بنجاح! 🎉",
          text: "مرحباً بك في لوحة التحكم",
          icon: "success",
          button: "عرض البيانات",
        }).then(() => {
          setLoggedIn(true);
          // التوجيه المباشر إلى لوحة التحكم
          window.location.href = "/#/parent-dashboard";
        });
      } else {
        swal({
          title: "لم يتم العثور على بيانات!",
          text: "يرجى التحقق من رقم ولي الأمر",
          icon: "error",
          button: "حسناً",
        });
      }
    } catch (e) {
      if (e.response) {
        if (e.response.status === 404) {
          swal({ 
            title: "لم يتم العثور على طالب", 
            text: "يرجى التحقق من رقم ولي الأمر أو التواصل معنا", 
            icon: "error", 
            button: "حسناً" 
          });
        } else if (e.response.status === 400) {
          swal({ 
            title: "رقم ولي الأمر مطلوب", 
            text: "يرجى إدخال رقم ولي الأمر", 
            icon: "error", 
            button: "حسناً" 
          });
        }
      } else {
        swal({
          title: "خطأ في الشبكة!",
          text: "يرجى المحاولة مرة أخرى أو التواصل مع الدعم الفني.",
          icon: "error",
          button: "حسناً",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const getFieldStatus = (fieldName) => {
    if (touched[fieldName] && errors[fieldName]) {
      return 'error';
    }
    if (touched[fieldName] && formData[fieldName] && !errors[fieldName]) {
      return 'success';
    }
    return 'default';
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={`stylish-register-page ${style ? 'dark-mode' : ''}`}>
      <div className="form-box">
        <div className="form-panel">
          <form onSubmit={handleLogin} noValidate>
            <div className="title-wrapper">
              <h2>تسجيل دخول <span>ولي الأمر</span></h2>
              <p className="step-description">
                أدخل رقم هاتفك للاطلاع على بيانات ابنك/ابنتك
              </p>
            </div>

            <div className={`input-field ${getFieldStatus('pphone')}`}>
              <input 
                id="pphone" 
                type="number" 
                placeholder=" " 
                value={formData.pphone} 
                name="pphone"
                onChange={handleInputChange}
                onBlur={handleBlur}
                required 
              />
              <label htmlFor="pphone">
                <FontAwesomeIcon icon={faPhone} className="me-2" />
                رقم ولي الأمر
              </label>
              {getFieldStatus('pphone') === 'success' && (
                <FontAwesomeIcon icon={faCheckCircle} className="field-status-icon success" />
              )}
              {getFieldStatus('pphone') === 'error' && (
                <FontAwesomeIcon icon={faExclamationTriangle} className="field-status-icon error" />
              )}
              {errors.pphone && <small className="error-text">{errors.pphone}</small>}
            </div>
            
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner"></span>
                  <span>جاري البحث...</span>
                </>
              ) : (
                'عرض بيانات الطالب'
              )}
            </button>
          </form>
        </div>
        
        <div className="welcome-panel">
          <div className="welcome-content">
            <h2>مرحباً ولي الأمر! 👨‍👩‍👧‍👦</h2>
            <p>تابع تقدم ابنك/ابنتك في التعلم واطلع على حضورهم وغيابهم في جميع الكورسات.</p>
            
            <div className="parent-features">
              <div className="feature-item">
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faGraduationCap} />
                </div>
                <div className="feature-text">
                  <h4>متابعة الحضور</h4>
                  <p>اطلع على حضور وغياب ابنك في جميع الكورسات</p>
                </div>
              </div>
              
              <div className="feature-item">
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faUser} />
                </div>
                <div className="feature-text">
                  <h4>الإحصائيات</h4>
                  <p>معدلات الحضور والدرجات في كل مادة</p>
                </div>
              </div>
            </div>
            
            <div className="login-links">
              <Link to="/loginuser" className="reset-link">تسجيل دخول الطالب</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParentLogin;
